import { useEffect, useMemo, useRef, useState } from "react";
import { GlBasemap } from "../lib/basemap";
import {
  MapContainer,
  TileLayer,
  Marker,
  useMap,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import { useTheme } from "../lib/theme";
import { renderToStaticMarkup } from "react-dom/server";
import { X, ArrowsOutSimple, MapPin, Check } from "@phosphor-icons/react";

/**
 * Fullscreen pin-drop map — opens as a modal over the editor when the
 * owner needs to manually place a pin (e.g. the address geocoded to a
 * centroid or has no house number). Tap anywhere to set the pin,
 * drag to refine, hit "Use this spot" to commit.
 *
 * Designed as the escape hatch for the strict full-address rule:
 * if an address can't be made complete (festival lot, park, etc.),
 * the owner drops the pin manually and we trust those coords without
 * re-validating the typed address.
 *
 * Real bug report May 2026: owner pin map was too small to confidently
 * place a pin on a specific corner. User explicitly asked: "the map to
 * manually drop the pin needs to be bigger, maybe full screen. It can
 * be smaller at first, but if you click to move it, it needs to go
 * full screen".
 */

function pinIcon() {
  const html = renderToStaticMarkup(
    <div className="truck-pin">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M3 6h11v10H3zM14 9h4l3 3v4h-7z" stroke="white" strokeWidth="1.8" strokeLinejoin="round" fill="none" />
        <circle cx="7" cy="17" r="2" fill="white" />
        <circle cx="17" cy="17" r="2" fill="white" />
      </svg>
    </div>,
  );
  return L.divIcon({ html, className: "truck-marker", iconSize: [34, 34], iconAnchor: [17, 34] });
}

function Tiles() {
  const { theme } = useTheme();
  return <GlBasemap dark={theme === "dark"} key={theme} />;
}

function RecenterTo({ target, zoom = 16 }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.setView([target.lat, target.lng], zoom, { animate: true });
  }, [target, map, zoom]);
  return null;
}

/** Captures any single click on the map and lifts the new lat/lng up. */
function ClickToSet({ onPick }) {
  useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

/**
 * iOS Safari fallback — real bug report Oct 2026 (mobile owner: "can't
 * click on map to adjust placement"). Leaflet's synthesized click can
 * silently fail on iOS Safari, so we also listen for raw touch taps and
 * compute the lat/lng ourselves. If Leaflet's click DOES fire too, the
 * pin is just set twice to the same spot — harmless.
 */
function TouchTapToSet({ onPick }) {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    let start = null;
    const onStart = (e) => {
      start =
        e.touches && e.touches.length === 1
          ? { x: e.touches[0].clientX, y: e.touches[0].clientY, t: Date.now() }
          : null;
    };
    const onEnd = (e) => {
      if (!start) return;
      const t = e.changedTouches && e.changedTouches[0];
      const s = start;
      start = null;
      if (!t) return;
      // Ignore taps on zoom buttons / attribution / the marker itself.
      if (e.target.closest && e.target.closest(".leaflet-control, .leaflet-marker-pane")) return;
      const moved = Math.abs(t.clientX - s.x) > 12 || Math.abs(t.clientY - s.y) > 12;
      const slow = Date.now() - s.t > 600;
      if (moved || slow) return; // was a pan or long-press, not a tap
      const rect = el.getBoundingClientRect();
      const latlng = map.containerPointToLatLng(
        L.point(t.clientX - rect.left, t.clientY - rect.top),
      );
      onPick({ lat: latlng.lat, lng: latlng.lng });
    };
    el.addEventListener("touchstart", onStart, { passive: true });
    el.addEventListener("touchend", onEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onStart);
      el.removeEventListener("touchend", onEnd);
    };
  }, [map, onPick]);
  return null;
}

/**
 * Fullscreen modal map. Closed by tapping × or pressing Escape.
 * Returns the final lat/lng via onCommit; tapping × discards changes.
 */
export default function FullscreenPinPicker({
  initialLat,
  initialLng,
  onCommit,
  onCancel,
  testIdPrefix = "fullscreen-pin",
}) {
  const icon = useMemo(() => pinIcon(), []);
  const markerRef = useRef(null);
  // Default to Lancaster PA centre if no initial pin so the user has
  // SOMETHING on screen to tap.
  const [pin, setPin] = useState({
    lat: initialLat ?? 40.0379,
    lng: initialLng ?? -76.3055,
  });
  const hasInitial = initialLat != null && initialLng != null;

  // Lock body scroll while the modal is open, restore on close.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  // Esc closes the modal — standard a11y expectation.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onCancel?.();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  return (
    <div
      className="fixed inset-0 z-[10000] bg-background"
      role="dialog"
      aria-modal="true"
      aria-label="Drop a pin on the map"
      data-testid={`${testIdPrefix}-modal`}
    >
      {/* Top bar — close, instructions, confirm */}
      <header className="absolute top-0 inset-x-0 z-10 flex items-center gap-2 px-3 py-2.5 bg-background/95 backdrop-blur-md border-b border-border pt-[calc(env(safe-area-inset-top)+0.625rem)]">
        <button
          type="button"
          onClick={onCancel}
          className="h-9 w-9 grid place-items-center rounded-full hover:bg-muted text-foreground"
          aria-label="Close map"
          data-testid={`${testIdPrefix}-close`}
        >
          <X size={20} weight="bold" />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold leading-tight truncate">
            Tap the map to set your pin
          </div>
          <div className="text-[11px] text-muted-foreground tabular-nums leading-tight truncate">
            {pin.lat.toFixed(5)}, {pin.lng.toFixed(5)}
          </div>
        </div>
        <button
          type="button"
          onClick={() => onCommit?.(pin)}
          className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:opacity-90 transition"
          data-testid={`${testIdPrefix}-commit`}
        >
          <Check size={14} weight="bold" />
          Use this spot
        </button>
      </header>

      {/* Full-bleed map */}
      <div className="absolute inset-0 pt-14" style={{ paddingTop: "calc(3.5rem + env(safe-area-inset-top))" }}>
        <MapContainer
          center={[pin.lat, pin.lng]}
          zoom={hasInitial ? 17 : 12}
          scrollWheelZoom
          tapHold={false}
          className="h-full w-full"
        >
          <Tiles />
          <RecenterTo target={pin} zoom={hasInitial ? 17 : 12} />
          <ClickToSet onPick={setPin} />
          <TouchTapToSet onPick={setPin} />
          <Marker
            position={[pin.lat, pin.lng]}
            icon={icon}
            draggable
            ref={markerRef}
            eventHandlers={{
              dragend: () => {
                const m = markerRef.current;
                if (!m) return;
                const p = m.getLatLng();
                setPin({ lat: p.lat, lng: p.lng });
              },
            }}
          />
        </MapContainer>
      </div>

      {/* Subtle bottom helper text — keeps the UX self-explanatory on
          a first encounter without cluttering the header. */}
      <footer className="absolute bottom-0 inset-x-0 z-10 bg-background/95 backdrop-blur-md border-t border-border px-4 py-2 text-[12px] text-muted-foreground flex items-center gap-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
        <MapPin weight="duotone" className="h-4 w-4 text-primary flex-none" />
        <span>Tap anywhere to move the pin, or drag it to fine-tune.</span>
      </footer>
    </div>
  );
}

/**
 * Inline picker with an "Open fullscreen" affordance. Renders the
 * existing small draggable-pin map when collapsed; clicking the
 * expand button (or the map itself) opens the fullscreen modal.
 */
export function ExpandablePinPicker({ lat, lng, onChange, height = 180, testIdPrefix = "pin-picker" }) {
  const icon = useMemo(() => pinIcon(), []);
  const markerRef = useRef(null);
  const [local, setLocal] = useState({ lat, lng });
  const [fullscreen, setFullscreen] = useState(false);

  useEffect(() => {
    if (lat != null && lng != null) setLocal({ lat, lng });
  }, [lat, lng]);

  const hasPin = local.lat != null && local.lng != null;

  return (
    <>
      <div
        className="relative rounded-xl overflow-hidden border border-border"
        style={{ height }}
        data-testid={`${testIdPrefix}-inline`}
      >
        {hasPin ? (
          <MapContainer
            center={[local.lat, local.lng]}
            zoom={15}
            scrollWheelZoom={false}
            className="h-full w-full"
            // Disable map dragging in the inline view — it's small, the
            // user will fat-finger it. They open fullscreen to actually
            // move the pin.
            dragging={false}
            doubleClickZoom={false}
            zoomControl={false}
          >
            <Tiles />
            <RecenterTo target={local} />
            <Marker
              position={[local.lat, local.lng]}
              icon={icon}
              draggable={false}
              // Real iOS bug Oct 2026: owners tap directly ON the pin to
              // "fix" it — the interactive marker swallowed the tap so the
              // fullscreen picker never opened. Marker must be inert here.
              interactive={false}
              ref={markerRef}
            />
          </MapContainer>
        ) : (
          <div className="h-full w-full grid place-items-center bg-muted/40 text-xs text-muted-foreground">
            No pin yet — click below to place one
          </div>
        )}

        {/* Overlay click target — clicking ANYWHERE on the inline map
            opens the fullscreen picker. Big, obvious, hard to miss. */}
        <button
          type="button"
          onClick={() => setFullscreen(true)}
          className="absolute inset-0 z-[700] grid place-items-center bg-background/0 hover:bg-background/40 transition group"
          aria-label={hasPin ? "Edit pin on fullscreen map" : "Drop a pin on the map"}
          data-testid={`${testIdPrefix}-expand`}
        >
          <span className="opacity-0 group-hover:opacity-100 transition flex items-center gap-1.5 px-3 h-9 rounded-full bg-background text-foreground text-xs font-bold border border-border shadow-sm">
            <ArrowsOutSimple size={14} weight="bold" />
            {hasPin ? "Edit on fullscreen map" : "Drop pin manually"}
          </span>
        </button>

        {/* Persistent corner badge — visible even before hover so
            mobile users know the map is interactive. */}
        <span
          className="absolute top-2 right-2 z-[400] pointer-events-none inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-background/95 text-foreground text-[11px] font-bold border border-border shadow-sm"
          aria-hidden
        >
          <ArrowsOutSimple size={11} weight="bold" />
          Tap to expand
        </span>
      </div>

      {fullscreen && (
        <FullscreenPinPicker
          initialLat={local.lat}
          initialLng={local.lng}
          onCommit={(next) => {
            setLocal(next);
            onChange?.(next);
            setFullscreen(false);
          }}
          onCancel={() => setFullscreen(false)}
          testIdPrefix={testIdPrefix}
        />
      )}
    </>
  );
}
